import { ElementType, ForwardedRef, forwardRef } from 'react'
import {
  PolymorphicForwardRefExoticComponent,
  PolymorphicPropsWithoutRef
} from './polymorphic'
import { Spinner } from './Spinner'
import { classNames } from './utils'

const defaultElement = 'button'

interface OwnButtonProps {
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'tertiary-danger'
    | 'danger'
    | 'ghost'
    | 'ghost-secondary'
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'
  leftIcon?: JSX.Element
  rightIcon?: JSX.Element
  outline?: boolean
  fullWidth?: boolean
  loading?: boolean
  disabled?: boolean
}

export type ButtonProps<T extends ElementType = typeof defaultElement> =
  PolymorphicPropsWithoutRef<OwnButtonProps, T>

export const Button: PolymorphicForwardRefExoticComponent<
  OwnButtonProps,
  typeof defaultElement
> = forwardRef(function Button<T extends ElementType = typeof defaultElement>(
  {
    variant = 'primary',
    size = 'md',
    leftIcon,
    rightIcon,
    outline,
    fullWidth,
    loading,
    disabled,
    children,
    as,
    ...props
  }: ButtonProps<T>,
  ref: ForwardedRef<Element>
) {
  // TODO: Figure out why we can't omit the className
  // TODO: Remove this once the above is fixed
  if (props.className) {
    throw new Error('You cannot add classNames to the button')
  }

  const isDisabled = disabled || loading
  const Element: ElementType = as || defaultElement

  return (
    <Element
      {...props}
      ref={ref}
      disabled={isDisabled}
      className={classNames(
        'helix-btn inline-flex items-center justify-center hover:no-underline',
        {
          // Variants
          // When creating a new variant, base it off of the primary variant.

          // Primary
          // Solid
          'helix-btn--primary': variant === 'primary' && !outline,
          'hover:border-transparent hover:bg-brand-primary focus:bg-brand-primary active:border-transparent active:bg-green-700':
            variant === 'primary' && !outline && !isDisabled,
          // Primary
          // Outline
          'helix-btn helix-btn--secondary': variant === 'primary' && outline,
          'hover:helix-text-brand focus:helix-text-brand hover:border-brand-primary focus:border-brand-primary active:border-green-600 active:text-green-600':
            variant === 'primary' && outline && !isDisabled,

          // Secondary
          // Solid
          'border-transparent bg-gray-200 text-gray-600':
            variant === 'secondary' && !outline,
          'hover:border-transparent hover:bg-gray-300 focus:bg-gray-300 active:border-transparent active:bg-gray-400':
            variant === 'secondary' && !outline && !isDisabled,
          // Secondary
          // Outline
          'border-gray-300 text-gray-600': variant === 'secondary' && outline,
          'hover:border-gray-200 focus:border-gray-200 active:border-gray-300':
            variant === 'secondary' && outline && !isDisabled,

          // Tertiary
          // Solid
          'helix-text-brand border-transparent bg-gray-100':
            variant === 'tertiary' && !outline,
          'hover:helix-text-brand focus:helix-text-brand active:helix-text-brand hover:border-transparent active:border-transparent':
            variant === 'tertiary' && !outline && !isDisabled,
          // Tertiary
          // Outline
          'helix-text-brand border-gray-200': variant === 'tertiary' && outline,
          'hover:helix-text-brand focus:helix-text-brand active:helix-text-brand':
            variant === 'tertiary' && outline && !isDisabled,

          // Tertiary Danger
          // Solid
          'border-transparent bg-gray-100 text-red-600':
            variant === 'tertiary-danger' && !outline,
          'hover:border-transparent hover:text-red-700 focus:text-red-700 active:border-transparent active:text-red-500':
            variant === 'tertiary-danger' && !outline && !isDisabled,

          // Tertiary Danger
          // Outline
          'border-gray-200 text-red-500':
            variant === 'tertiary-danger' && outline,
          'hover:text-red-700 focus:text-red-700 active:text-red-500':
            variant === 'tertiary-danger' && outline && !isDisabled,

          // Danger
          // Solid
          'helix-btn--danger': variant === 'danger' && !outline,

          // Danger
          // Outline
          'border-red-600 text-red-600': variant === 'danger' && outline,
          'hover:border-red-700 hover:text-red-700 focus:border-red-700 focus:text-red-700 active:border-red-600 active:text-red-600':
            variant === 'danger' && outline && !isDisabled,

          // ghost
          // Solid
          'border-transparent bg-transparent text-gray-600':
            variant === 'ghost' && !outline,
          'outline-none hover:border-gray-200 hover:bg-gray-200 focus:bg-gray-300 active:border-transparent active:bg-gray-300':
            variant === 'ghost' && !outline && !isDisabled,

          // ghost Secondary
          // Solid
          'border-transparent bg-transparent !text-brand-primary':
            variant === 'ghost-secondary' && !outline,
          'text-gray-600 hover:border-gray-200 hover:bg-gray-200 focus:bg-gray-300 active:border-transparent active:bg-gray-300':
            variant === 'ghost-secondary' && !outline && !isDisabled,

          // Size
          'helix-btn--small rounded': size === 'xxs',
          'rounded px-2.5 py-1.5 text-xs': size === 'xs',
          'rounded-md px-3 py-2 text-sm leading-4': size === 'sm',
          'rounded-md px-4 py-2 text-sm': size === 'md',
          'rounded-md px-4 py-2 text-base': size === 'lg',
          'rounded-md px-6 py-3 text-base': size === 'xl',
          'rounded-md px-6 py-3 text-xl': size === '2xl',
          'rounded-md px-6 py-4 text-2xl': size === '3xl',

          // Width
          'w-full': fullWidth,

          // Disabled
          'cursor-pointer': !isDisabled,
          'cursor-not-allowed opacity-50': isDisabled
        }
      )}>
      {!leftIcon && !rightIcon && loading && (
        <span
          className={classNames('mr-1 flex-shrink-0', {
            'h-4 w-4': ['xs', 'sm'].includes(size),
            'h-5 w-5': ['md', 'lg', 'xl'].includes(size)
          })}>
          <Spinner />
        </span>
      )}
      {leftIcon && (
        <span
          className={classNames('-ml-1 mr-2 flex-shrink-0', {
            'h-4 w-4': ['xs', 'sm'].includes(size),
            'h-5 w-5': ['md', 'lg', 'xl'].includes(size)
          })}>
          {loading ? <Spinner /> : leftIcon}
        </span>
      )}
      {children}
      {rightIcon && (
        <span
          className={classNames('-mr-1 ml-2 flex-shrink-0', {
            'h-4 w-4': ['xs', 'sm'].includes(size),
            'h-5 w-5': ['md', 'lg', 'xl'].includes(size)
          })}>
          {loading ? <Spinner /> : rightIcon}
        </span>
      )}
    </Element>
  )
})
